import type {
  CandidateDecision,
  DecisionOutcome,
  GroupCandidate,
  GroupCandidatesResponse,
  MeResponse,
} from '@stalvi/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { GroupMembersGrid } from '../components/GroupMembers';
import { GroupsNav } from '../components/GroupsNav';
import { Header } from '../components/Header';
import { useToast } from '../components/Toast';
import { ApiError, api } from '../lib/api';

const CANDIDATES_LIMIT = 100;

// confidence is an unbounded composite RANK score (retailer agreement + price convergence
// + name completeness), not a 0..1 probability — show it as a score, never a percentage.
function confidenceScore(c: number | null): string {
  return c == null ? '—' : c.toFixed(1);
}

function sizeLabel(netContent: string | null, unit: string | null): string {
  return [netContent, unit].filter(Boolean).join(' ') || '—';
}

function CandidateDetailPanel({ candidateKey }: { candidateKey: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['group-candidate', candidateKey],
    queryFn: () => api.groupCandidateDetail(candidateKey),
  });
  if (isLoading) return <div className="px-4 py-3 text-text-secondary text-sm">Cargando miembros…</div>;
  if (error || !data)
    return (
      <p role="alert" className="px-4 py-3 text-danger text-sm">
        No pudimos cargar el detalle.
      </p>
    );
  return (
    <div className="px-4 py-3 bg-bg-subtle">
      <GroupMembersGrid members={data.members} />
    </div>
  );
}

function CandidateRow({
  candidate,
  selected,
  onToggleSelect,
  expanded,
  onToggleExpand,
  onDecide,
  busy,
}: {
  candidate: GroupCandidate;
  selected: boolean;
  onToggleSelect: () => void;
  expanded: boolean;
  onToggleExpand: () => void;
  onDecide: (verdict: 'confirm' | 'reject') => void;
  busy: boolean;
}) {
  return (
    <li className="divide-y divide-border-subtle">
      <div className="flex items-start gap-3 px-4 py-3">
        <input
          type="checkbox"
          className="mt-1 shrink-0"
          checked={selected}
          onChange={onToggleSelect}
          aria-label={`Seleccionar candidato ${candidate.candidateKey}`}
        />
        <button
          type="button"
          className="mt-0.5 shrink-0 text-text-secondary"
          onClick={onToggleExpand}
          aria-label={expanded ? 'Ocultar miembros' : 'Ver miembros'}
          aria-expanded={expanded}
        >
          {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>
        <div className="min-w-0 flex-1">
          <p className="font-medium leading-snug line-clamp-2">{candidate.nameSample ?? '—'}</p>
          <p className="text-sm text-text-secondary truncate">
            {candidate.brandNorm ?? '—'} · {sizeLabel(candidate.netContent, candidate.unit)}
          </p>
          <p className="mono text-xs text-text-tertiary mt-1 break-all">
            {candidate.memberEan13s.join(', ')}
          </p>
        </div>
        <div className="shrink-0 text-right text-xs text-text-tertiary">
          <p>
            <span className="pill pill-neutral">#{candidate.rank}</span>
          </p>
          <p className="mt-1">score {confidenceScore(candidate.confidence)}</p>
          <p>{candidate.retailerCount} cadenas</p>
        </div>
        <div className="shrink-0 flex flex-col gap-1">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={busy}
            onClick={() => onDecide('confirm')}
          >
            Confirmar
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={busy}
            onClick={() => onDecide('reject')}
          >
            Rechazar
          </button>
        </div>
      </div>
      {expanded && <CandidateDetailPanel candidateKey={candidate.candidateKey} />}
    </li>
  );
}

export function GroupsReview({ me, onSignOut }: { me: MeResponse; onSignOut: () => void }) {
  const qc = useQueryClient();
  const { show } = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<DecisionOutcome[]>([]);

  const { data, isLoading, error } = useQuery<GroupCandidatesResponse>({
    queryKey: ['group-candidates'],
    queryFn: () => api.groupCandidates(CANDIDATES_LIMIT),
  });

  const decide = useMutation({
    mutationFn: (decisions: CandidateDecision[]) => api.decideCandidates(decisions),
    onSuccess: (res, decisions) => {
      setOutcomes(res.outcomes);
      const decided = new Set(decisions.map((d) => d.candidateKey));
      setSelected((prev) => new Set([...prev].filter((k) => !decided.has(k))));
      const failed = res.outcomes.filter((o) => !o.ok).length;
      if (failed) show(`${failed} de ${res.outcomes.length} fallaron`, 'error');
      else show(`${res.outcomes.length} procesados`, 'info');
      void qc.invalidateQueries({ queryKey: ['group-candidates'] });
    },
    onError: (e) => {
      show(e instanceof ApiError ? e.apiMessage : 'Hubo un problema', 'error');
    },
  });

  const items = data?.items ?? [];

  function toggleSelect(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function decideOne(candidateKey: string, verdict: 'confirm' | 'reject') {
    decide.mutate([{ candidateKey, verdict }]);
  }

  function decideSelected(verdict: 'confirm' | 'reject') {
    const decisions = [...selected].map((candidateKey) => ({ candidateKey, verdict }));
    if (decisions.length) decide.mutate(decisions);
  }

  return (
    <div className="stalvi-app flex flex-col">
      <Header me={me} onSignOut={onSignOut} />
      <GroupsNav />
      <main className="flex-1 px-6 py-6 overflow-auto">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h1 className="text-xl font-semibold">Cola de revisión</h1>
            {selected.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-text-secondary">{selected.size} seleccionados</span>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={decide.isPending}
                  onClick={() => decideSelected('confirm')}
                >
                  Aprobar seleccionados
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={decide.isPending}
                  onClick={() => decideSelected('reject')}
                >
                  Rechazar seleccionados
                </button>
              </div>
            )}
          </div>

          {outcomes.some((o) => !o.ok) && (
            <ul role="alert" className="card p-4 mb-4 text-sm text-danger flex flex-col gap-1">
              {outcomes
                .filter((o) => !o.ok)
                .map((o) => (
                  <li key={o.candidateKey} className="break-all">
                    {o.candidateKey}: {o.error ?? 'error desconocido'}
                  </li>
                ))}
            </ul>
          )}

          {error && (
            <p role="alert" className="text-danger mb-4">
              Hubo un problema, intenta de nuevo.
            </p>
          )}

          {isLoading ? (
            <div className="card p-8">Cargando…</div>
          ) : items.length === 0 ? (
            <div className="card p-8 text-center text-text-secondary">No hay candidatos por revisar.</div>
          ) : (
            <ul className="card p-0 divide-y divide-border-subtle">
              {items.map((c) => (
                <CandidateRow
                  key={c.candidateKey}
                  candidate={c}
                  selected={selected.has(c.candidateKey)}
                  onToggleSelect={() => toggleSelect(c.candidateKey)}
                  expanded={expanded === c.candidateKey}
                  onToggleExpand={() => setExpanded((e) => (e === c.candidateKey ? null : c.candidateKey))}
                  onDecide={(v) => decideOne(c.candidateKey, v)}
                  busy={decide.isPending}
                />
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
